import { useCallback, useEffect, useState } from "react";
import { fetchAdminEmployees, type AdminEmployeeRow } from "../api/adminApi";
import type { Employee } from "../types";
import { getAuthToken } from "../auth";
import { provinceOptions } from "../data";

export type AdminEmployeesFilters = {
  search?: string;
  company_id?: number;
  province?: string;
  branch?: string;
  system_id?: string;
  status?: string;
};

function resolveProvinceLabel(slug: string): string {
  if (!slug) return "";
  return provinceOptions.find((option) => option.value === slug)?.label ?? slug;
}

function mapApiEmployee(row: AdminEmployeeRow): Employee {
  return {
    id: `emp-${row.id}`,
    recordId: row.id,
    photo: row.photo,
    name: row.name,
    systemId: row.system_id,
    companyMatricule: row.company_matricule,
    company: row.company,
    province: row.province || "—",
    branch: row.branch?.trim() || "Non renseignée",
    status: row.status,
  };
}

export function useAdminEmployees() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async (filters: AdminEmployeesFilters = {}) => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setEmployees([]);
      setTotal(0);
      setError("Connexion requise pour charger les employés.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const response = await fetchAdminEmployees({
        search: filters.search,
        company_id: filters.company_id,
        province: filters.province ? resolveProvinceLabel(filters.province) : undefined,
        branch: filters.branch,
        system_id: filters.system_id,
        status: filters.status,
      });
      const mapped = response.employees.map(mapApiEmployee);
      setEmployees(mapped);
      setTotal(response.total);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Impossible de charger les employés.";
      setError(message);
      setEmployees([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load({});
  }, [load]);

  return { employees, total, loading, error, usingApi, reload: load };
}